import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import KPISection from './components/KPISection';
import ChartsSection from './components/ChartsSection';
import MediaAnalyticsSection from './components/MediaAnalyticsSection';
import ClientFinanceSection from './components/ClientFinanceSection';
import DriversManagementSection from './components/DriversManagementSection';
import PointHubManagementSection from './components/PointHubManagementSection';
import VenuePlansManagementSection from './components/VenuePlansManagementSection';
import VenueCampaignsSection from './components/VenueCampaignsSection';
import UrbanCampaignsSection from './components/UrbanCampaignsSection';
import { useDashboardData } from './hooks/useDashboardData';
import { LLMSettingsSection } from './components/LLMSettingsSection';
import { ProductSettingsSection } from './components/ProductSettingsSection';
import { fetchPlans, createPlan, updatePlan, deletePlan } from '../api/plans';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import { CreateAdminMasterModal } from './components/CreateAdminMasterModal';
import BannersManagementSection from './components/BannersManagementSection';
import { MobileBannersSection } from './components/MobileBannersSection';
import { MobileBannersXaigooSection } from './components/MobileBannersXaigooSection';
import { Shield, Image, Brain, Smartphone, Bell } from 'lucide-react';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { DateTimePicker } from "@/components/ui/date-time-picker";

// Componente de gerenciamento de planos
const PlansManagementSection: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    description: '',
    expiration_days: '30', // Quantidade de dias para expiração
    is_active: true,
    features: '', // Campo para features (separadas por linha)
    device_limit: '1', // Campo para limite de dispositivos
    is_custom: false, // Campo para plano personalizado
    whatsapp_url: '', // Campo para URL do WhatsApp
    qt_cupom: '0' // Quantidade de promoções permitidas
  });

  const loadPlans = async () => {
    try {
      // Primeiro tentar com display_order, se falhar usar created_at
      let { data, error } = await supabase
        .from('plans')
        .select('*')
        .order('display_order', { ascending: true });

      // Se der erro (possivelmente coluna não existe), tentar sem display_order
      if (error && error.message?.includes('display_order')) {
        console.warn('Coluna display_order não encontrada, usando created_at');
        const fallback = await supabase
          .from('plans')
          .select('*')
          .order('created_at', { ascending: false });
        data = fallback.data;
        error = fallback.error;
      }

      if (error) throw error;
      setPlans(data || []);
    } catch (err) {
      console.error('Erro ao buscar planos:', err);
      setMessage('❌ Erro ao carregar planos: ' + (err as Error).message);
    }
  };

  // Função para atualizar a ordem dos planos
  const updatePlanOrder = async (planId: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('plans')
        .update({ display_order: newOrder })
        .eq('id', planId);

      if (error) throw error;
    } catch (err) {
      console.error('Erro ao atualizar ordem do plano:', err);
      throw err;
    }
  };

  // Função para lidar com o drag and drop
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(plans);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Atualizar estado local imediatamente para feedback visual
    setPlans(items);

    try {
      // Atualizar ordem no banco de dados
      const updatePromises = items.map((plan, index) =>
        updatePlanOrder(plan.id, index + 1)
      );

      await Promise.all(updatePromises);
      setMessage('✅ Ordem dos planos atualizada com sucesso!');

      // Recarregar para garantir consistência
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Erro ao atualizar ordem:', err);
      setMessage('❌ Erro ao atualizar ordem dos planos');
      // Reverter mudanças em caso de erro
      loadPlans();
    }
  };

  // Função para fazer upload da imagem para o Supabase Storage
  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      setUploadingImage(true);

      // Gerar nome único para o arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `plan-images/${fileName}`;

      // Fazer upload para o Supabase Storage
      const { data, error } = await supabase.storage
        .from('plan-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      // Obter URL pública da imagem
      const { data: { publicUrl } } = supabase.storage
        .from('plan-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      throw new Error('Erro ao fazer upload da imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  // Função para lidar com a seleção de imagem
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        setMessage('❌ Por favor, selecione apenas arquivos de imagem');
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('❌ A imagem deve ter no máximo 5MB');
        return;
      }

      setSelectedImage(file);

      // Criar preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para remover imagem selecionada
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      let imageUrl = null;

      // Se há uma nova imagem selecionada, fazer upload
      if (selectedImage) {
        imageUrl = await uploadImage(selectedImage);
      } else if (editingPlan?.image_url) {
        // Se está editando e já tem imagem, manter a existente
        imageUrl = editingPlan.image_url;
      }

      // Converter expiration_days para número
      const expiration_days = parseInt(formData.expiration_days) || 30;

      // Converter features de string para array (separadas por linha)
      const featuresArray = formData.features
        .split('\n')
        .map(feature => feature.trim())
        .filter(feature => feature.length > 0);

      // Definir ordem para novos planos (último + 1)
      const maxOrder = plans.length > 0 ? Math.max(...plans.map(p => p.display_order || 0)) : 0;

      const planData = {
        title: formData.title,
        price: formData.is_custom ? 0 : parseFloat(formData.price),
        description: formData.description,
        expiration_days: expiration_days,
        expiration_minutes: 0, // Não usamos mais minutos, apenas dias
        is_active: formData.is_active,
        image_url: imageUrl,
        features: featuresArray,
        device_limit: formData.is_custom ? null : (parseInt(formData.device_limit) || 1),
        QT_cupom: formData.is_custom ? null : (parseInt(formData.qt_cupom) || 0),
        is_custom: formData.is_custom,
        whatsapp_url: formData.is_custom ? formData.whatsapp_url : null,
        display_order: editingPlan ? editingPlan.display_order : maxOrder + 1,
        created_at: new Date().toISOString()
      };

      if (editingPlan) {
        // Editar plano existente
        await updatePlan(editingPlan.id, planData);
      } else {
        // Criar novo plano
        await createPlan(planData);
      }

      setMessage(`✅ Plano ${editingPlan ? 'atualizado' : 'criado'} com sucesso!`);
      setIsModalOpen(false);
      setEditingPlan(null);
      setSelectedImage(null);
      setImagePreview(null);
      setFormData({
        title: '',
        price: '',
        description: '',
        expiration_days: '30',
        is_active: true,
        features: '',
        device_limit: '1',
        is_custom: false,
        whatsapp_url: '',
        qt_cupom: '0'
      });
      loadPlans();
    } catch (err) {
      setMessage('❌ Erro: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan: any) => {
    // Obter o valor de dias diretamente do plano
    const expirationDays = plan.expiration_days || 30;

    setEditingPlan(plan);
    setFormData({
      title: plan.title,
      price: plan.price.toString(),
      description: plan.description,
      expiration_days: expirationDays.toString(),
      is_active: plan.is_active,
      features: plan.features ? plan.features.join('\n') : '',
      device_limit: plan.device_limit ? plan.device_limit.toString() : '1',
      is_custom: plan.is_custom || false,
      whatsapp_url: plan.whatsapp_url || '',
      qt_cupom: (plan.QT_cupom != null ? String(plan.QT_cupom) : '0')
    });

    // Se o plano já tem imagem, mostrar preview
    if (plan.image_url) {
      setImagePreview(plan.image_url);
    } else {
      setImagePreview(null);
    }

    setSelectedImage(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (planId: string) => {
    console.log('handleDelete chamado com ID:', planId);

    if (!planId) {
      setMessage('❌ ID do plano não encontrado');
      return;
    }

    // Primeiro, tentar uma exclusão simples
    const simpleDelete = confirm('Tem certeza que deseja excluir este plano?');
    if (!simpleDelete) {
      console.log('Exclusão cancelada pelo usuário');
      return;
    }

    try {
      setLoading(true);
      setMessage('🔄 Excluindo plano...');

      console.log('Iniciando exclusão do plano:', planId);

      // Tentar excluir o plano diretamente
      const { error } = await supabase
        .from('plans')
        .delete()
        .eq('id', planId);

      if (error) {
        console.error('Erro do Supabase:', error);

        // Se for erro de foreign key, oferecer alternativa
        if (error.code === '23503') {
          setLoading(false);

          const disableInstead = confirm(
            'Não é possível excluir este plano pois ele possui dependências (pagamentos ou usuários associados).\n\n' +
            'Deseja DESATIVAR o plano em vez de excluí-lo?\n\n' +
            'Planos desativados não aparecem para novos usuários, mas mantêm o histórico.'
          );

          if (disableInstead) {
            try {
              setLoading(true);
              setMessage('🔄 Desativando plano...');

              const { error: updateError } = await supabase
                .from('plans')
                .update({ is_active: false })
                .eq('id', planId);

              if (updateError) throw updateError;

              setMessage('✅ Plano desativado com sucesso!');
              await loadPlans();
              setTimeout(() => setMessage(''), 3000);
              return;

            } catch (updateErr) {
              console.error('Erro ao desativar plano:', updateErr);
              setMessage('❌ Erro ao desativar plano: ' + (updateErr as Error).message);
              return;
            } finally {
              setLoading(false);
            }
          } else {
            setMessage('❌ Exclusão cancelada');
            return;
          }
        }

        throw error;
      }

      console.log('Plano excluído com sucesso');
      setMessage('✅ Plano excluído com sucesso!');

      // Recarregar lista de planos
      await loadPlans();

      // Limpar mensagem após 3 segundos
      setTimeout(() => setMessage(''), 3000);

    } catch (err) {
      console.error('Erro ao excluir plano:', err);
      setMessage('❌ Erro ao excluir plano: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadPlans();
  }, []);

  return (
    <div className="w-full bg-white rounded-lg shadow p-6 mb-8">
      <div className="mb-6">
        <p className="text-gray-600">
          Crie e gerencie planos de assinatura com diferentes prazos de expiração.
        </p>
      </div>

      <div className="mb-4">
        <button
          onClick={() => {
            setEditingPlan(null);
            setSelectedImage(null);
            setImagePreview(null);
            setFormData({
              title: '',
              price: '',
              description: '',
              expiration_days: '30',
              is_active: true,
              features: '',
              device_limit: '1',
              is_custom: false,
              whatsapp_url: '',
              qt_cupom: '0'
            });
            setIsModalOpen(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center justify-center text-sm font-medium transition-colors duration-200"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Novo Plano</span>
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-md text-sm mb-4 ${message.includes('✅')
          ? 'bg-green-100 text-green-800 border border-green-200'
          : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
          {message}
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="plans">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-4"
            >
              {plans.map((plan, index) => (
                <Draggable key={plan.id} draggableId={plan.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-white border rounded-lg p-4 shadow-sm transition-shadow ${snapshot.isDragging ? 'shadow-lg' : 'hover:shadow-md'
                        }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className="cursor-grab active:cursor-grabbing p-2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                          </svg>
                        </div>

                        {/* Imagem */}
                        <div className="flex-shrink-0">
                          {plan.image_url ? (
                            <img
                              src={plan.image_url}
                              alt={plan.title}
                              className="w-16 h-16 object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center">
                              <span className="text-gray-500 text-xs">Sem img</span>
                            </div>
                          )}
                        </div>

                        {/* Informações do Plano */}
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <h3 className="font-semibold text-lg">{plan.title}</h3>
                            <p className="text-gray-600 text-sm truncate">{plan.description}</p>
                          </div>

                          <div>
                            <p className="text-sm text-gray-500">Preço</p>
                            <p className="font-semibold text-green-600">
                              {plan.is_custom ? 'Sob consulta' : formatCurrency(plan.price)}
                            </p>
                          </div>

                          <div>
                            <p className="text-sm text-gray-500">Expiração</p>
                            <p className="font-medium">
                              {plan.expiration_minutes > 0
                                ? `${plan.expiration_minutes} min`
                                : `${plan.expiration_days} dias`}
                            </p>
                            {!plan.is_custom && (
                              <p className="text-sm text-gray-500">Dispositivos: {plan.device_limit || 1}</p>
                            )}
                          </div>

                          <div>
                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium mb-2 ${plan.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                              {plan.is_active ? 'Ativo' : 'Inativo'}
                            </span>
                            {plan.is_custom && (
                              <span className="inline-block px-2 py-1 rounded text-xs font-medium mb-2 ml-2 bg-purple-100 text-purple-800">
                                Personalizado
                              </span>
                            )}
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(plan)}
                                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Editar
                              </button>
                              <button
                                onClick={() => {
                                  console.log('Tentando excluir plano:', plan.id, plan.title);
                                  handleDelete(plan.id);
                                }}
                                disabled={loading}
                                className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {loading ? 'Excluindo...' : 'Excluir'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Modal de criação/edição */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {editingPlan ? 'Editar Plano' : 'Criar Novo Plano'}
              </h3>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_custom"
                  checked={formData.is_custom}
                  onChange={(e) => setFormData({ ...formData, is_custom: e.target.checked, whatsapp_url: e.target.checked ? formData.whatsapp_url : '', price: e.target.checked ? '0' : formData.price })}
                  className="mr-1"
                />
                <label htmlFor="is_custom" className="text-sm font-medium text-purple-600">Personalizado</label>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Upload de Imagem */}
              <div>
                <label className="block text-sm font-medium mb-2">Imagem do Plano</label>

                {/* Preview da imagem */}
                {imagePreview && (
                  <div className="mb-3">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-md border"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="mt-2 text-red-600 text-sm hover:text-red-800 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remover imagem
                    </button>
                  </div>
                )}

                {/* Input de arquivo */}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="w-full px-3 py-2 border rounded-md"
                  disabled={uploadingImage}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formatos aceitos: JPG, PNG, GIF. Máximo: 5MB
                </p>
                {uploadingImage && (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Fazendo upload...
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Título do Plano</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>

              {!formData.is_custom && (
                <div>
                  <label className="block text-sm font-medium mb-1">Preço (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  rows={3}
                  required
                />
              </div>

              {!formData.is_custom && (
                <div>
                  <label className="block text-sm font-medium mb-1">Limite de Dispositivos</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.device_limit}
                    onChange={(e) => setFormData({ ...formData, device_limit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Quantidade máxima de dispositivos que o usuário pode ativar com este plano
                  </p>
                </div>
              )}

              {!formData.is_custom && (
                <div>
                  <label className="block text-sm font-medium mb-1">Quantidade de promoções permitida</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.qt_cupom}
                    onChange={(e) => setFormData({ ...formData, qt_cupom: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Número máximo de promoções que o usuário poderá criar no plano
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Features/Benefícios</label>
                <textarea
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  rows={6}
                  placeholder="Digite cada feature em uma linha separada. Exemplo:&#10;Acesso completo à plataforma&#10;Suporte técnico&#10;Atualizações automáticas&#10;Expira em 30 dias"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Digite cada feature em uma linha separada. Deixe vazio para usar features padrão.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Prazo de Expiração (dias)</label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.expiration_days}
                    onChange={(e) => setFormData({ ...formData, expiration_days: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Ex: 30"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Quantidade de dias até o plano expirar após a ativação
                </p>
              </div>

              {formData.is_custom && (
                <div>
                  <label className="block text-sm font-medium mb-1">URL do WhatsApp</label>
                  <input
                    type="url"
                    value={formData.whatsapp_url}
                    onChange={(e) => setFormData({ ...formData, whatsapp_url: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="https://wa.me/5511999999999?text=Olá, gostaria de um plano personalizado"
                    required={formData.is_custom}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL do WhatsApp para onde o usuário será redirecionado ao escolher este plano
                  </p>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm">Plano ativo</label>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={loading || uploadingImage}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Salvando...' : (editingPlan ? 'Atualizar' : 'Criar')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingPlan(null);
                    setSelectedImage(null);
                    setImagePreview(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};


// Componente para seções recolhíveis
interface CollapsibleSectionProps {
  title: string;
  icon?: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  isExpanded,
  onToggle,
  children
}) => {
  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 pr-6 bg-white rounded-t-lg shadow hover:bg-gray-50 transition-colors border-b"
      >
        <div className="flex items-center gap-3">
          {icon}
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        </div>
        <svg
          className={`w-5 h-5 text-gray-600 transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : 'rotate-0'
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`transition-all duration-300 overflow-hidden mt-4 ${isExpanded ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
      >
        {children}
      </div>
    </div>
  );
};


const AdminDashboard: React.FC = () => {
  const { dashboardData, isLoading, error, refresh } = useDashboardData();
  const { user } = useAuth();

  // Estados para controlar expansão/recolhimento de cada seção
  const [sectionsExpanded, setSectionsExpanded] = useState({
    banners: false,
    bannersXaigoo: false,
    clientFinance: true,
    drivers: false,
    pointHub: false,
    venuePlans: false,
    plans: true,
    kpi: true,
    charts: true,
    mediaAnalytics: true,
    venueCampaigns: false,
    urbanCampaigns: false,
    productSettings: false,
    llmSettings: false,
    bannersMobile: false,
    maintenance: false
  });

  // Estado para o modal de criação de Admin Master
  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);

  // Hook para configurações do sistema (incluindo manutenção)
  const { settings } = useSystemSettings();
  const [updatingMaintenance, setUpdatingMaintenance] = useState(false);
  const [updatingLandingMaintenance, setUpdatingLandingMaintenance] = useState(false);

  // Função para alternar modo de manutenção
  const toggleMaintenance = async () => {
    try {
      setUpdatingMaintenance(true);
      const newValue = !settings.maintenance_mode;

      // Atualiza a tabela de configurações. Como deve haver apenas um registro,
      // atualizamos usando uma condição verdadeira para pegar o registro único.
      const { error } = await supabase
        .from('system_settings')
        .update({ maintenance_mode: newValue })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Hack seguro para atualizar todos (deve ser 1)

      if (error) throw error;

    } catch (err) {
      console.error('Erro ao atualizar modo de manutenção:', err);
      alert('Erro ao atualizar modo de manutenção. Verifique as permissões.');
    } finally {
      setUpdatingMaintenance(false);
    }
  };

  // Função para alternar modo de manutenção da Landing Page
  const toggleLandingMaintenance = async () => {
    try {
      setUpdatingLandingMaintenance(true);
      const newValue = !settings.landing_maintenance_mode;

      const { error } = await supabase
        .from('system_settings')
        .update({ landing_maintenance_mode: newValue })
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (error) throw error;
    } catch (err) {
      console.error('Erro ao atualizar modo de manutenção da Landing:', err);
      alert('Erro ao atualizar modo de manutenção da Landing.');
    } finally {
      setUpdatingLandingMaintenance(false);
    }
  };

  const toggleSection = (section: keyof typeof sectionsExpanded) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <>
      {/* Navbar Fixa */}
      <nav className="fixed top-0 left-0 right-0 bg-[hsl(142,76%,36%)] shadow-lg z-50">
        <div className="max-w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo à esquerda */}
            <div className="flex items-center">
              <img
                src="https://link.letswatch.com.br/storage/27001/new-watch-Smart-Media-logo-white.webp"
                alt="Watch Logo"
                className="h-16 w-auto"
              />
            </div>

            {/* Botões à direita */}
            {user && (
              <div className="flex items-center gap-3">
                {/* Botão de criação de Admin Master - apenas para master@admin.com */}
                {user.email === 'master@admin.com' && (
                  <button
                    onClick={() => setIsCreateAdminModalOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm"
                    title="Gerenciar Admin Masters"
                  >
                    <Shield className="w-4 h-4" />
                    <span className="hidden md:inline">Admin Master</span>
                  </button>
                )}
                <button
                  onClick={() => window.location.href = '/'}
                  className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm"
                  title="Ir para Painel Geral"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2V7z" />
                  </svg>
                  <span className="hidden md:inline">Painel Geral</span>
                </button>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm font-medium text-white hidden md:inline">{user.email}</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      // Limpar sessão do Supabase
                      await supabase.auth.signOut();
                      // Limpar qualquer cache de autenticação
                      sessionStorage.clear();
                      localStorage.removeItem('supabase.auth.token');
                      // Forçar redirecionamento para a página de login
                      window.location.replace('/');
                    } catch (error) {
                      console.error('Erro ao fazer logout:', error);
                      // Mesmo com erro, redirecionar
                      window.location.replace('/');
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  title="Sair da conta"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="hidden md:inline">Sair</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Conteúdo principal com padding-top para compensar a navbar fixa */}
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="w-full p-4 md:p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold">Dashboard administrativo</h1>
          </div>

          {/* Seção de Gerenciamento de Banners */}
          <CollapsibleSection
            title="Gerenciamento de Banners"
            icon={<Image className="w-6 h-6 text-slate-600" />}
            isExpanded={sectionsExpanded.banners}
            onToggle={() => toggleSection('banners')}
          >
            <BannersManagementSection />
          </CollapsibleSection>

          {/* Seção de Alerta Push Xaigoo */}
          <CollapsibleSection
            title="Alerta Push Xaigoo"
            icon={<Bell className="w-6 h-6 text-slate-600" />}
            isExpanded={sectionsExpanded.bannersXaigoo}
            onToggle={() => toggleSection('bannersXaigoo')}
          >
            <MobileBannersXaigooSection />
          </CollapsibleSection>

          <CollapsibleSection
            title="Configurações de Produtos"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            isExpanded={sectionsExpanded.productSettings}
            onToggle={() => toggleSection('productSettings')}
          >
            <ProductSettingsSection />
          </CollapsibleSection>

          {/* Seção: Configurações de API LLM */}
          <CollapsibleSection
            title="Configurações de APIs LLM (IA)"
            icon={<Brain className="w-6 h-6 text-slate-600" />}
            isExpanded={sectionsExpanded.llmSettings}
            onToggle={() => toggleSection('llmSettings')}
          >
            <LLMSettingsSection />
          </CollapsibleSection>

          {/* Seção: Banners Mobile (Copilot) */}
          <CollapsibleSection
            title="Banners Mobile (Copilot)"
            icon={<Smartphone className="w-6 h-6 text-slate-600" />}
            isExpanded={sectionsExpanded.bannersMobile}
            onToggle={() => toggleSection('bannersMobile')}
          >
            <MobileBannersSection />
          </CollapsibleSection>

          {/* Seção de Finanças */}
          <CollapsibleSection
            title="Finanças e Clientes"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            isExpanded={sectionsExpanded.clientFinance}
            onToggle={() => toggleSection('clientFinance')}
          >
            <ClientFinanceSection />
          </CollapsibleSection>

          {/* Seção de Gerenciamento de Motoristas */}
          <CollapsibleSection
            title="Gerenciamento de Motoristas"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
            isExpanded={sectionsExpanded.drivers}
            onToggle={() => toggleSection('drivers')}
          >
            <DriversManagementSection />
          </CollapsibleSection>

          {/* Seção de Gerenciamento Point Hub */}
          <CollapsibleSection
            title="Gerenciamento Point Hub"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-1 4h1" />
              </svg>
            }
            isExpanded={sectionsExpanded.pointHub}
            onToggle={() => toggleSection('pointHub')}
          >
            <PointHubManagementSection />
          </CollapsibleSection>

          {/* Seção de Gerenciamento Slot Venue */}
          <CollapsibleSection
            title="Gerenciamento Slot Venue"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            }
            isExpanded={sectionsExpanded.venuePlans}
            onToggle={() => toggleSection('venuePlans')}
          >
            <VenuePlansManagementSection />
          </CollapsibleSection>

          {/* Seção de Gerenciamento de Campanhas Venue */}
          <CollapsibleSection
            title="Gerenciamento de Campanhas Venue"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            }
            isExpanded={sectionsExpanded.venueCampaigns}
            onToggle={() => toggleSection('venueCampaigns')}
          >
            <VenueCampaignsSection />
          </CollapsibleSection>

          {/* Seção de Gerenciamento de Campanhas Urban */}
          <CollapsibleSection
            title="Gerenciamento de Campanhas Urban"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            }
            isExpanded={sectionsExpanded.urbanCampaigns}
            onToggle={() => toggleSection('urbanCampaigns')}
          >
            <UrbanCampaignsSection />
          </CollapsibleSection>

          {/* Seção de Gerenciamento Manutenção */}
          <CollapsibleSection
            title="Gerenciamento Manutenção"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            isExpanded={sectionsExpanded.maintenance}
            onToggle={() => toggleSection('maintenance')}
          >
            <div className="w-full bg-white rounded-lg shadow p-6">
              <div className="space-y-6">
                {/* Toggle Manutenção Dashboard */}
                <div className="flex flex-col gap-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${settings.maintenance_mode ? 'bg-orange-100' : 'bg-gray-100'}`}>
                        <svg className={`w-5 h-5 ${settings.maintenance_mode ? 'text-orange-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">Manutenção Dashboard</h3>
                        <p className="text-sm text-gray-500">Ativar modo de manutenção na tela do dashboard do cliente</p>
                      </div>
                    </div>
                    <button
                      onClick={toggleMaintenance}
                      disabled={updatingMaintenance}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${settings.maintenance_mode
                        ? 'bg-orange-500 focus:ring-orange-500'
                        : 'bg-gray-300 focus:ring-gray-400'
                        } ${updatingMaintenance ? 'opacity-50 cursor-wait' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${settings.maintenance_mode ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {/* Toggle Manutenção Landing Page */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${settings.landing_maintenance_mode ? 'bg-orange-100' : 'bg-gray-100'}`}>
                        <svg className={`w-5 h-5 ${settings.landing_maintenance_mode ? 'text-orange-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">Manutenção Landing Page</h3>
                        <p className="text-sm text-gray-500">Ativar modo de manutenção na Landing Page (página inicial)</p>
                      </div>
                    </div>
                    <button
                      onClick={toggleLandingMaintenance}
                      disabled={updatingLandingMaintenance}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${settings.landing_maintenance_mode
                        ? 'bg-orange-500 focus:ring-orange-500'
                        : 'bg-gray-300 focus:ring-gray-400'
                        } ${updatingLandingMaintenance ? 'opacity-50 cursor-wait' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${settings.landing_maintenance_mode ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {/* Configuração de Data de Retorno */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Previsão de Retorno (Opcional)</label>
                    <div className="flex gap-3 items-center">
                      <div className="max-w-xs w-full">
                        <DateTimePicker
                          date={settings.maintenance_return_time ? new Date(settings.maintenance_return_time) : undefined}
                          setDate={async (date) => {
                            const newTime = date ? date.toISOString() : null;
                            try {
                              const { error } = await supabase
                                .from('system_settings')
                                .update({ maintenance_return_time: newTime })
                                .neq('id', '00000000-0000-0000-0000-000000000000');

                              if (error) throw error;
                            } catch (err) {
                              console.error('Erro ao atualizar data de retorno:', err);
                              alert('Erro ao salvar data de retorno.');
                            }
                          }}
                          placeholder="Clique para definir o retorno"
                        />
                      </div>

                      <button
                        onClick={async () => {
                          try {
                            const { error } = await supabase
                              .from('system_settings')
                              .update({ maintenance_return_time: null })
                              .neq('id', '00000000-0000-0000-0000-000000000000');

                            if (error) throw error;
                          } catch (err) {
                            console.error('Erro ao limpar data:', err);
                          }
                        }}
                        className="px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded border border-red-200 transition-colors h-10 flex items-center justify-center font-medium"
                        title="Limpar previsão"
                      >
                        Limpar
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Esta data será exibida na tela de manutenção para os clientes informando quando o sistema deve retornar.
                    </p>
                  </div>
                </div>

                {/* Status Indicator */}
                {settings.maintenance_mode && (
                  <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg mt-4">
                    <svg className="w-5 h-5 text-orange-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-orange-800">
                      Modo de manutenção ativo - Os clientes verão a tela de bloqueio.
                      {settings.maintenance_return_time && ` Retorno previsto: ${new Date(settings.maintenance_return_time).toLocaleString('pt-BR')}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {error && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg font-medium">
              Erro ao carregar dados do dashboard.
            </div>
          )}

          {/* Seção de Gerenciamento de Planos */}
          <CollapsibleSection
            title="Gerenciamento de Planos"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            isExpanded={sectionsExpanded.plans}
            onToggle={() => toggleSection('plans')}
          >
            <PlansManagementSection />
          </CollapsibleSection>

          {/* Seção de KPIs */}
          <CollapsibleSection
            title="Indicadores de Desempenho (KPIs)"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
            isExpanded={sectionsExpanded.kpi}
            onToggle={() => toggleSection('kpi')}
          >
            <KPISection loading={isLoading} data={dashboardData} />
          </CollapsibleSection>

          {/* Seção de Gráficos */}
          <CollapsibleSection
            title="Gráficos e Análises"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            }
            isExpanded={sectionsExpanded.charts}
            onToggle={() => toggleSection('charts')}
          >
            <ChartsSection loading={isLoading} data={dashboardData} />
          </CollapsibleSection>

          {/* Seção de Analytics de Mídia */}
          <CollapsibleSection
            title="Analytics de Mídia"
            icon={
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            }
            isExpanded={sectionsExpanded.mediaAnalytics}
            onToggle={() => toggleSection('mediaAnalytics')}
          >
            <MediaAnalyticsSection loading={isLoading} data={dashboardData} />
          </CollapsibleSection>
          {isLoading && (
            <div className="w-full h-64 bg-white rounded-lg shadow flex items-center justify-center text-gray-400 text-xl animate-pulse mt-8">
              Carregando dados do sistema...
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-400">
            © 2026 Watch Technologies | Smart Media. Todos os direitos reservados.
          </p>
        </div>
      </footer>

      {/* Modal de Criação de Admin Master */}
      <CreateAdminMasterModal
        open={isCreateAdminModalOpen}
        onOpenChange={setIsCreateAdminModalOpen}
        currentUserEmail={user?.email || ''}
      />
    </>
  );
};

export default AdminDashboard;
